import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { driverSupabase } from '@/integrations/supabase/driver-client';
import { ensureAdminAlertsChannel } from '@/lib/pushChannel';
import { dedupNotificationKey } from '@/lib/notificationDedup';

type FcmData = Record<string, string>;

const LIFECYCLE_EVENTS = new Set([
  'driver_assigned',
  'driver_arrived',
  'trip_started',
  'trip_completed',
  'trip_cancelled',
]);

function eventTypeOf(data: Record<string, any>) {
  return String(data?.event_type || data?.type || '');
}

function isTripOffer(data: Record<string, any>) {
  const type = eventTypeOf(data);
  return type === 'trip_offer' || type === 'trip_offer_cancelled';
}

function isLifecycle(data: Record<string, any>) {
  return LIFECYCLE_EVENTS.has(eventTypeOf(data));
}

function routeFor(data: FcmData): string {
  const type = eventTypeOf(data);
  const requestId = data.request_id;
  const bookingRef = data.booking_number || data.booking_id;

  if (type === 'trip_offer' || type === 'trip_offer_cancelled') {
    if (requestId) return `/driver/home?offer=${encodeURIComponent(requestId)}`;
    return '/driver/home';
  }

  if (LIFECYCLE_EVENTS.has(type)) {
    if (type === 'trip_cancelled') return '/driver/home';
    return bookingRef ? `/driver/trips?focus=${encodeURIComponent(bookingRef)}` : '/driver/trips';
  }

  return '/driver/notifications';
}

function notificationTitle(data: Record<string, any>) {
  const type = eventTypeOf(data);
  return (
    data.title ||
    data.notification?.title ||
    ({
      driver_assigned: 'Trip Assigned',
      driver_arrived: 'Arrived at Pickup',
      trip_started: 'Trip Started',
      trip_completed: 'Trip Completed',
      trip_cancelled: 'Trip Cancelled',
    } as Record<string, string>)[type] ||
    'Driver Alert'
  );
}

function notificationBody(data: Record<string, any>) {
  return String(data.body || data.message || data.notification?.body || data.booking_number || 'Tap to view details');
}

// In-memory dedup guard: collapses the multi-rule fan-out from the Admin
// Portal so we only bump the badge / dispatch one `driver:notification`
// event per logical occurrence within a 60s bucket.
const seenKeys = new Map<string, number>();
const SEEN_TTL_MS = 5 * 60_000;
function shouldEmit(key: string): boolean {
  const now = Date.now();
  // Opportunistic GC
  if (seenKeys.size > 200) {
    for (const [k, t] of seenKeys) {
      if (now - t > SEEN_TTL_MS) seenKeys.delete(k);
    }
  }
  const prev = seenKeys.get(key);
  if (prev && now - prev < SEEN_TTL_MS) return false;
  seenKeys.set(key, now);
  return true;
}

function keyForData(data: Record<string, any>): string {
  // Prefer server-provided dedup_key so FCM + Realtime + poll collapse to
  // the same key regardless of arrival time / 60s bucket drift.
  const serverKey = data?.dedup_key ? String(data.dedup_key) : '';
  if (serverKey) return `dk:${serverKey}`;

  return dedupNotificationKey({
    id: '',
    type: String(data?.type || data?.event_type || ''),
    recipient_id: String(data?.recipient_id || ''),
    booking_number: data?.booking_number || null,
    booking_id: data?.booking_id || null,
    // Anchor the bucket to server time when available, otherwise fall back
    // to now(). Prevents FCM (arrival time) vs Realtime (row.created_at)
    // landing in different 60s buckets.
    created_at: data?.created_at || data?.sent_at || new Date().toISOString(),
  });
}

export function useDriverNotifications(driverId?: string) {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== 'android') return;

    let cancelled = false;
    const cleanup: Array<{ remove: () => Promise<void> }> = [];

    (async () => {
      try {
        await ensureAdminAlertsChannel();

        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { LocalNotifications } = await import('@capacitor/local-notifications');

        const onReceived = await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
          if (cancelled) return;
          const data = (notification.data || {}) as FcmData;
          if (isTripOffer(data)) return;

          // The FCM `notification` block on the `admin-alerts` channel is
          // rendered by Android itself; do NOT schedule a duplicate
          // LocalNotification here. Just bump the in-app badge.
          if (!shouldEmit(keyForData(data))) return;
          window.dispatchEvent(new CustomEvent('driver:notification', {
            detail: {
              source: 'fcm',
              type: eventTypeOf(data),
              booking_number: data.booking_number || data.booking_id,
              data,
            },
          }));
        });

        const onTap = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = (action.notification?.data || {}) as FcmData;
          if (isTripOffer(data)) return;
          navRef.current(routeFor(data));
        });

        const onLocalTap = await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
          const data = (action.notification?.extra || {}) as FcmData;
          if (isTripOffer(data)) return;
          navRef.current(routeFor(data));
        });

        cleanup.push(onReceived, onTap, onLocalTap);
      } catch (err) {
        console.warn('[driver-notifications] init failed', err);
      }
    })();

    return () => {
      cancelled = true;
      cleanup.forEach((l) => { try { l.remove(); } catch { /* noop */ } });
    };
  }, []);

  useEffect(() => {
    if (!driverId) return;

    const channel = driverSupabase
      .channel(`driver-notifications-${driverId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${driverId}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (row?.recipient_type !== 'driver') return;
          if (row?.channel !== 'in_app') return;
          if (isTripOffer(row)) return;

          const key = row?.dedup_key
            ? `dk:${String(row.dedup_key)}`
            : dedupNotificationKey({
                id: row?.id ?? '',
                type: row?.type,
                recipient_id: row?.recipient_id,
                booking_number: row?.booking_number,
                booking_id: row?.booking_id,
                dedup_key: row?.dedup_key,
                created_at: row?.created_at || new Date().toISOString(),
              });
          if (!shouldEmit(key)) return;

          window.dispatchEvent(new CustomEvent('driver:notification', {
            detail: {
              source: 'realtime',
              type: row?.type,
              booking_number: row?.booking_number,
              row,
            },
          }));
        },
      )
      .subscribe();

    const poll = setInterval(async () => {
      try {
        const { data } = await driverSupabase
          .from('notifications' as any)
          .select('id')
          .eq('recipient_id', driverId)
          .eq('recipient_type', 'driver')
          .eq('channel', 'in_app')
          .eq('is_read', false)
          .limit(1);
        if (Array.isArray(data) && data.length > 0) {
          const pollKey = `poll:${(data[0] as any).id}`;
          if (shouldEmit(pollKey)) {
            window.dispatchEvent(new CustomEvent('driver:notification', { detail: { source: 'poll' } }));
          }
        }
      } catch { /* noop */ }
    }, 45000);

    return () => {
      driverSupabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [driverId]);
}
